import { useState, useEffect } from 'react';
import { SettingsSubNav } from '../components/SettingsSubNav';
import { CheckCircle2, Save } from 'lucide-react';

export function ModulesIndustrySettingsPage() {
  const [profileType, setProfileType] = useState('it_services');
  const [enabledModules, setEnabledModules] = useState<Record<string, boolean>>({
    crm: true,
    projects: true,
    timesheets: true,
    pos: false,
    inventory: true,
    purchasing: true,
    manufacturing: false,
    quality: false,
    batchSerial: false,
    commissions: true,
    loyalty: false,
    notifications: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/accounting/org/modules')
      .then((res) => res.json())
      .then((data) => {
        if (data.ok || data.success) {
          if (data.data?.profileType) setProfileType(data.data.profileType);
          if (data.data?.enabledModules) setEnabledModules(data.data.enabledModules);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleProfileSelect = (profId: string) => {
    setProfileType(profId);
    if (profId === 'retail_pos') {
      setEnabledModules({
        crm: true,
        projects: false,
        timesheets: false,
        pos: true,
        inventory: true,
        purchasing: true,
        manufacturing: false,
        quality: false,
        batchSerial: true,
        commissions: true,
        loyalty: true,
        notifications: true,
      });
    } else if (profId === 'it_services') {
      setEnabledModules({
        crm: true,
        projects: true,
        timesheets: true,
        pos: false,
        inventory: true,
        purchasing: true,
        manufacturing: false,
        quality: false,
        batchSerial: false,
        commissions: true,
        loyalty: false,
        notifications: true,
      });
    } else if (profId === 'process_manufacturing') {
      setEnabledModules({
        crm: true,
        projects: true,
        timesheets: false,
        pos: false,
        inventory: true,
        purchasing: true,
        manufacturing: true,
        quality: true,
        batchSerial: true,
        commissions: false,
        loyalty: false,
        notifications: true,
      });
    }
  };

  const handleToggle = (moduleKey: string) => {
    setEnabledModules((prev) => ({
      ...prev,
      [moduleKey]: !prev[moduleKey],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/accounting/org/modules', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileType, enabledModules }),
      });
      const data = await res.json();
      if (res.ok && (data.ok || data.success)) {
        setToast('Industry profile & active module configuration saved!');
        setTimeout(() => setToast(null), 3000);
      } else {
        alert(data.error?.message || 'Failed to save module settings.');
      }
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="text-slate-900 dark:text-slate-100">
      <SettingsSubNav />

      <div className="p-6 max-w-5xl mx-auto space-y-6">
        {toast && (
          <div className="p-4 bg-success/10 dark:bg-success border border-success/25 dark:border-success/40 rounded-xl text-success-text dark:text-success-text text-sm flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-success-text" />
            {toast}
          </div>
        )}

        <div>
          <h2 className="text-lg font-bold">Modules & Industry Profiles</h2>
          <p className="text-xs text-slate-500">Select an industry profile to pre-configure defaults, or selectively enable/disable specific modules.</p>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-500">Loading module settings...</div>
        ) : (
          <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
              <label className="block text-xs font-bold text-slate-800 dark:text-slate-200">Industry Profile Preset</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { id: 'it_services', label: 'IT & Services' },
                  { id: 'marketing_agency', label: 'Marketing Agency' },
                  { id: 'trading_distribution', label: 'Trading & Distribution' },
                  { id: 'retail_pos', label: 'Retail / POS' },
                  { id: 'process_manufacturing', label: 'Process Mfg' },
                  { id: 'oil_petroleum', label: 'Oil & Petroleum' },
                  { id: 'custom', label: 'Custom' },
                ].map((prof) => (
                  <button
                    key={prof.id}
                    type="button"
                    onClick={() => handleProfileSelect(prof.id)}
                    className={`p-3 rounded-xl border text-xs font-bold text-center transition ${
                      profileType === prof.id
                        ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 shadow-xs'
                        : 'border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300'
                    }`}
                  >
                    {prof.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
              <h3 className="text-sm font-bold">Module Enablement Toggles</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.keys(enabledModules).map((key) => (
                  <label
                    key={key}
                    className="flex items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer"
                  >
                    <span className="text-xs font-semibold capitalize text-slate-800 dark:text-slate-200">
                      {key.replace(/([A-Z])/g, ' $1')}
                    </span>
                    <input
                      type="checkbox"
                      checked={enabledModules[key]}
                      onChange={() => handleToggle(key)}
                      className="w-4 h-4 text-indigo-600 rounded"
                    />
                  </label>
                ))}
              </div>

              <div className="flex justify-end pt-4 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleSave}
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-2"
                >
                  <Save className="w-4 h-4" /> Save Module Configuration
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ModulesIndustrySettingsPage;
